/**
 * Maps device CSV data (rows of semicolon-separated values) to DataViewer sections.
 * Used by DeviceExport (preview), DeviceImportHistory (report display),
 * and DeviceImport (dry-run preview).
 */
import { ViewerSection, ViewerColumn } from "@/components/common/data-viewer/DataViewerModels";
import { ImportResult } from "@/data/models/CSV";
import { CSV_SEPARATOR } from "@/utils/Utils";
import {
  COLOR_IMPORT_STATUS_NEW,
  COLOR_IMPORT_STATUS_UPDATED,
  COLOR_IMPORT_STATUS_NO_CHANGE,
  COLOR_IMPORT_STATUS_ERROR,
  COLOR_IMPORT_STATUS_DUPLICATE,
  COLOR_IMPORT_STATUS_DELETED,
  COLOR_IMPORT_STATUS_RESTORED,
} from "@/utils/colors";

/** Column keys matching the device CSV column order. */
const COLUMN_KEYS = [
  "category", "template", "inventoryNumber", "serialNumber",
  "manufacturer", "code", "power", "meter",
  "electricityMeterManufacturer", "electricityMeterNumber",
  "purchaseDate", "availability", "location", "comment", "meterReading",
  "locationName",
];

/** Column keys for report CSV (with STATUS as the last column). */
const REPORT_COLUMN_KEYS = [...COLUMN_KEYS, "status"];

/** Column keys for report CSV with error details (one more column). */
const REPORT_WITH_ERROR_COLUMN_KEYS = [...REPORT_COLUMN_KEYS, "error"];

/** Maps import result status values to display colors for the STATUS column. */
const STATUS_COLOR_MAP: Record<string, string> = {
  [ImportResult.NEW]: COLOR_IMPORT_STATUS_NEW,
  [ImportResult.UPDATED]: COLOR_IMPORT_STATUS_UPDATED,
  [ImportResult.NO_CHANGE]: COLOR_IMPORT_STATUS_NO_CHANGE,
  [ImportResult.ERROR]: COLOR_IMPORT_STATUS_ERROR,
  [ImportResult.DUPLICATE]: COLOR_IMPORT_STATUS_DUPLICATE,
  [ImportResult.DELETED]: COLOR_IMPORT_STATUS_DELETED,
  [ImportResult.RESTORED]: COLOR_IMPORT_STATUS_RESTORED,
};

/** The export-only branch name column, absent from reports written before it existed. */
const LOCATION_NAME_KEY = "locationName";

/**
 * Builds ViewerColumn definitions from localized CSV headers.
 *
 * @param headers - Localized header labels in CSV column order.
 * @param keys - Column keys in the same order.
 */
function buildColumns(headers: string[], keys: string[]): ViewerColumn[] {
  return keys.map((key, index) => ({
    key,
    label: headers[index] ?? key,
    searchable: true,
    visible: true,
    width: key === "comment" ? "200px" : key === "error" ? "250px" : undefined,
    colorMap: key === "status" ? STATUS_COLOR_MAP : undefined,
  }));
}

/**
 * Maps device CSV rows to a single ViewerSection for the DataViewer.
 *
 * @param headers - Localized header labels (from getLocalizedCSVHeaders or CSV_REPORT_HEADERS).
 * @param dataRows - Array of CSV row strings (semicolon-separated, without header row).
 * @param sectionId - Section identifier.
 * @param sectionName - Display name for the section.
 * @param includeStatus - Whether rows include a 16th STATUS column (import reports).
 * @param includeError - Whether rows may include a 17th ERROR column (dry-run previews with error details).
 */
export function mapDeviceCsvToSection(
  headers: string[],
  dataRows: string[],
  sectionId: string,
  sectionName: string,
  includeStatus: boolean,
  includeError = false,
): ViewerSection {
  const keys = includeError
    ? REPORT_WITH_ERROR_COLUMN_KEYS
    : includeStatus
      ? REPORT_COLUMN_KEYS
      : COLUMN_KEYS;

  // Reports stored before the STANDORTNAME column existed are one column short, with
  // their STATUS sitting where STANDORTNAME sits now. Recognise them by width and drop
  // the column again, so an archived report still lines up with its headers.
  const widestRow = dataRows.reduce(
    (widest, row) => Math.max(widest, row.split(CSV_SEPARATOR).length),
    0
  );
  const locationNameIndex = keys.indexOf(LOCATION_NAME_KEY);
  const isLegacyReport =
    locationNameIndex >= 0 && widestRow > 0 && widestRow <= keys.length - 1;

  const effectiveKeys = isLegacyReport
    ? keys.filter((key) => key !== LOCATION_NAME_KEY)
    : keys;
  const effectiveHeaders = isLegacyReport
    ? headers.filter((header, index) => index !== locationNameIndex)
    : headers;

  const columns = buildColumns(effectiveHeaders, effectiveKeys);

  const rows = dataRows.map((row) => {
    const values = row.split(CSV_SEPARATOR);
    const record: Record<string, string | number | null> = {};
    for (let i = 0; i < effectiveKeys.length; i++) {
      record[effectiveKeys[i]] = values[i]?.trim() ?? "";
    }
    return record;
  });

  return {
    id: sectionId,
    name: sectionName,
    type: "",
    columns,
    rows,
    expandedByDefault: true,
  };
}
